import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import { useAppSelector } from 'hooks/reduxHooks'
import getLoyaltyDisplayTier from 'loyalty/selectors/getLoyaltyDisplayTier'
import getLoyaltyTierDisplayName from 'loyalty/selectors/getLoyaltyTierDisplayName'
import type { PropsWithChildren, ReactNode } from 'react'
import React, { Fragment } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { nonNullable } from 'lib/array/arrayUtils'

export const AccommodationIndividualSelectRoomOptionTileDetailsContainer = styled(VerticalSpacer)``

type Props = {
  /**
   * positive integer
   */
  packageNumber?: number
  tagLineLabel?: ReactNode
  stayPayRateTier?: App.StayPayTier
  cancellationPolicy?: ReactNode
  variant: 'stacked' | 'carousel'
} & ({
  roomPackage?: App.HotelPackage
  selectedLoyaltyTargetUpgradePackage?: App.Package
  promotion?: undefined
} | {
  roomPackage?: undefined
  selectedLoyaltyTargetUpgradePackage?: undefined
  promotion?: App.BedbankPromotion
})

function AccommodationIndividualSelectRoomOptionTileDetails(props: PropsWithChildren<Props>) {
  const {
    children,
    packageNumber,
    tagLineLabel,
    roomPackage,
    promotion,
    stayPayRateTier,
    selectedLoyaltyTargetUpgradePackage,
    cancellationPolicy,
    variant,
  } = props

  const intl = useIntl()

  const name = roomPackage?.name ?? promotion?.name
  const targetUpgradeName = selectedLoyaltyTargetUpgradePackage?.name
  const displayTier = useAppSelector(getLoyaltyDisplayTier)

  const packageOptionLabel = packageNumber && variant === 'carousel'
    ? intl.formatMessage(
        {
          id: '7Mi5NsVG/i',
          defaultMessage: 'Package option {number}',
          description: 'Label showing the package option number in the room option carousel.',
        },
        { number: packageNumber },
      )
    : undefined

  const memberUpgradeLabel = targetUpgradeName
    ? intl.formatMessage(
        {
          id: '2FjAmCuPs3',
          defaultMessage: '{tierName} member upgrade',
          description: 'Label indicating that this option includes a loyalty member upgrade.',
        },
        { tierName: getLoyaltyTierDisplayName(displayTier) },
      )
    : undefined

  const upgradedToLabel = targetUpgradeName
    ? intl.formatMessage(
        {
          id: '+GJNF9Ml3A',
          defaultMessage: 'Upgraded to {upgradeName}',
          description: 'Label indicating the selected loyalty upgrade package name.',
        },
        { upgradeName: targetUpgradeName },
      )
    : undefined

  const nightFreeSuffix = stayPayRateTier
    ? intl.formatMessage(
        {
          id: 'dov8oO8ws7',
          defaultMessage: ', {count, plural, one {# night free} other {# nights free}}',
          description: 'Suffix appended to a package name indicating included free nights.',
        },
        { count: stayPayRateTier.freeNights },
      )
    : undefined

  const topLabels = nonNullable([
    packageOptionLabel,
    memberUpgradeLabel ? <b key="upgrade-label">{memberUpgradeLabel}</b> : undefined,
  ])

  return <AccommodationIndividualSelectRoomOptionTileDetailsContainer gap={12}>
    <VerticalSpacer gap={4}>
      {topLabels.length > 0 && <Caption variant="large">
        {topLabels.map((label, i) => <Fragment key={i}>
          {i > 0 && ' | '}
          {label}
        </Fragment>)}
      </Caption>}
      {!!tagLineLabel && <LabelGroup>{tagLineLabel}</LabelGroup>}
      {!!targetUpgradeName && <BodyText variant="large" weight="bold">
        {upgradedToLabel}
      </BodyText>}
      {!!name && <BodyText
        variant="large"
        weight="bold"
        colour={selectedLoyaltyTargetUpgradePackage ? 'neutral-four' : 'neutral-one'}
        strikeThrough={!!selectedLoyaltyTargetUpgradePackage}
      >
        {name}
        {nightFreeSuffix}
      </BodyText>}
      {cancellationPolicy}
    </VerticalSpacer>
    {children}
  </AccommodationIndividualSelectRoomOptionTileDetailsContainer>
}

export default AccommodationIndividualSelectRoomOptionTileDetails
